/**
 * EmitEventDialog — the human "emit event" affordance for the Events list
 * (frontend-entity-coverage §2.3, Event → **emit**). POSTs a new domain event
 * to `POST /api/events` (`11 §3.1`) through the sanctioned worker write path
 * (`useApiAction`), then invalidates the events / rule-runs / agent-runs caches
 * so the new event and anything the kernel spawns from it appear.
 *
 * Minimal by design: an event_type, an optional artifact id, and an optional
 * JSON payload (validated client-side before submit so a bad body never 400s).
 */
import * as React from "react";
import { useInvalidate } from "@refinedev/core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApiAction } from "../_shared/useApiAction";

export interface EmitEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmitEventDialog({ open, onOpenChange }: EmitEventDialogProps) {
  const { run, loading } = useApiAction();
  const invalidate = useInvalidate();
  const [eventType, setEventType] = React.useState("");
  const [artifactId, setArtifactId] = React.useState("");
  const [payload, setPayload] = React.useState("");
  const [jsonError, setJsonError] = React.useState<string | null>(null);

  const reset = () => {
    setEventType("");
    setArtifactId("");
    setPayload("");
    setJsonError(null);
  };

  const submit = async () => {
    let parsedPayload: unknown = undefined;
    if (payload.trim() !== "") {
      try {
        parsedPayload = JSON.parse(payload);
        setJsonError(null);
      } catch {
        setJsonError("Payload must be valid JSON.");
        return;
      }
    }
    try {
      await run("/events", {
        body: {
          event_type: eventType.trim(),
          artifact_id: artifactId.trim() || null,
          payload: parsedPayload,
        },
        invalidateResources: ["events", "rule-runs", "agent-runs"],
        successMessage: "Event emitted through the kernel.",
      });
      await invalidate({ resource: "events", invalidates: ["list"] });
      reset();
      onOpenChange(false);
    } catch {
      /* error toast surfaced by useApiAction; keep the dialog open */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Emit event</DialogTitle>
          <DialogDescription>
            Append a domain event to the kernel log. The kernel evaluates rules
            against it as if it occurred naturally.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="emit-event-type">Event type</Label>
            <Input
              id="emit-event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              placeholder="e.g. task.created"
              data-testid="emit-event-type"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emit-artifact-id">Artifact id (optional)</Label>
            <Input
              id="emit-artifact-id"
              value={artifactId}
              onChange={(e) => setArtifactId(e.target.value)}
              placeholder="art_…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emit-payload">Payload (JSON, optional)</Label>
            <Textarea
              id="emit-payload"
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              placeholder='{ "key": "value" }'
              rows={4}
              className="font-mono text-xs"
            />
            {jsonError ? (
              <p className="text-xs text-destructive">{jsonError}</p>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={loading || eventType.trim() === ""}
            data-testid="emit-event-submit"
          >
            {loading ? "Emitting…" : "Emit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmitEventDialog;
